import { XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { AutoRejectionRule } from "./flowData";

/** The minimal reason shape the original timeline used. */
interface SimpleReason {
  text: string;
  protected?: boolean;
}

interface RejectionBranchProps {
  /** Original shape — kept for backward compatibility. */
  reasons?: SimpleReason[];
  /** Richer auto-rejection rules from flowData. Takes precedence over `reasons`. */
  rules?: AutoRejectionRule[];
  /** Hide the dashed left connector (e.g. when rendered as a standalone card). */
  hideConnector?: boolean;
}

export function RejectionBranch({
  reasons,
  rules,
  hideConnector,
}: RejectionBranchProps) {
  const items: AutoRejectionRule[] =
    rules ?? (reasons ?? []).map((r) => ({ ...r, detail: "" }));

  return (
    <div className="flex items-stretch gap-0 md:gap-0">
      {/* Dashed horizontal connector (md+ only) */}
      {!hideConnector && (
        <div className="hidden md:flex items-center">
          <div className="w-8 border-t-2 border-dashed border-red-300" />
        </div>
      )}

      {/* Rejection card */}
      <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg px-4 py-3 flex-1 md:flex-none md:w-72">
        <div className="flex items-center gap-2 mb-2">
          <XCircle className="size-4 text-red-500 shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
            Auto-Rejection
          </span>
        </div>
        <ul className="space-y-2">
          {items.map((rule) => (
            <li
              key={rule.text}
              className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
            >
              <span className="mt-1.5 size-1 rounded-full bg-red-400 shrink-0" />
              <div className="min-w-0">
                <span>
                  {rule.text}
                  {rule.protected && (
                    <span className="text-xs text-red-400 ml-1">*</span>
                  )}
                </span>
                {rule.detail && (
                  <p className="text-xs text-red-500/80 dark:text-red-400/70 leading-relaxed">
                    {rule.detail}
                  </p>
                )}
                {rule.toggle && (
                  <Badge
                    variant="outline"
                    className="mt-1 text-[10px] px-1.5 py-0 font-mono border-red-300 text-red-600 dark:border-red-900/60 dark:text-red-400"
                  >
                    {rule.toggle}
                  </Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
